import type { ChatSpace, MemoryDoc, SpaceFile, SpaceOutput, UpsertChatSpaceInput } from "@codvero/shared";
import { ApiFailure, api, del, get, post } from "./client.ts";

/** The Chat section's spaces: instructions and reference files shared by a group of conversations. */
export const spacesApi = {
  list: () => get<ChatSpace[]>("/api/chat-spaces"),
  get: (id: string) => get<ChatSpace>(`/api/chat-spaces/${id}`),
  create: (input: UpsertChatSpaceInput) => post<ChatSpace>("/api/chat-spaces", input),
  update: (id: string, input: UpsertChatSpaceInput) => api<ChatSpace>(`/api/chat-spaces/${id}`, { method: "PUT", json: input }),
  remove: (id: string) => del(`/api/chat-spaces/${id}`),
  files: (id: string) => get<SpaceFile[]>(`/api/chat-spaces/${id}/files`),
  upload: async (id: string, file: File): Promise<SpaceFile> => {
    const fd = new FormData();
    fd.set("file", file, file.name);
    const res = await fetch(`/api/chat-spaces/${id}/files`, { method: "POST", body: fd, credentials: "same-origin" });
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string } & SpaceFile;
    if (!res.ok) throw new ApiFailure((body.error as never) ?? "internal", res.status, body.message);
    return body;
  },
  setHidden: (id: string, hidden: boolean) => api<ChatSpace>(`/api/chat-spaces/${id}/sidebar`, { method: "PUT", json: { hidden } }),
  memory: (id: string) => get<MemoryDoc>(`/api/chat-spaces/${id}/memory`),
  saveMemory: (id: string, content: string) => api<MemoryDoc>(`/api/chat-spaces/${id}/memory`, { method: "PUT", json: { content } }),
  pin: (id: string, pinned: boolean) => api<ChatSpace>(`/api/chat-spaces/${id}/pin`, { method: "PUT", json: { pinned } }),
  /** files the providers made in the space's conversations */
  outputs: (id: string) => get<SpaceOutput[]>(`/api/chat-spaces/${id}/outputs`),
  keepOutput: (id: string, path: string) => post<SpaceFile>(`/api/chat-spaces/${id}/outputs/keep`, { path }),
  removeFile: (id: string, name: string) => del(`/api/chat-spaces/${id}/files/${encodeURIComponent(name)}`),
};
